import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { format, isValid } from 'date-fns';

import { Box, Button, FieldSet, FormField, FormHelp, Input, Label, Legend } from '@zillow/constellation-10';
import { IconPlusCircleFilled } from '@zillow/constellation-icons';

import { PhoneVerificationSource } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertyEditV2WebApiHandler_phone } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useScreenSize } from '../../../hooks';
import { phonePatches } from '../../../hooks/queries/listingDetailsCache';
import { useGetListingDetails } from '../../../hooks/queries/properties';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';
import numberUtils from '../../../utils/numberUtils';
import propertyUtils from '../../../utils/propertyUtils';

import { DEFAULT_VERIFICATION_CONFIG } from '../../modals/VerificationModal/constants';

import { VerificationModal } from '../../modals/VerificationModal';

import PhoneNumberDisplayInfo from '../PhoneNumberDisplayInfo';

import type React from 'react';

type Props = {
  contactPhoneRef?: React.LegacyRef<HTMLDivElement>;
  isInputFormDisabled?: boolean;
};

const AddEditPhoneNumber = ({ contactPhoneRef, isInputFormDisabled = false }: Props) => {
  const router = useRouter();
  const { isScreenDesktop } = useScreenSize();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const contactPhone = listingDetails?.phone || '';
  const contactPhoneDateVerified = listingDetails?.phoneVerifiedOnMs
    ? new Date(listingDetails.phoneVerifiedOnMs).toString()
    : '';

  const [verifiedPhoneNumber, setVerifiedPhoneNumber] = useState<string>(contactPhone || '');
  const [datePhoneVerified, setDatePhoneVerified] = useState('');
  const [showPhoneVerificationModal, setShowPhoneVerificationModal] = useState(false);

  const isRoomForRent = propertyUtils.isRoomForRent({ listing: listingDetails });

  const { mutateAsync: editPhoneNumber } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_phone,
    skipToast: true,
    skipTracking: true,
    awaitRefetch: true,
    applyToCache: phonePatches,
    meta: { context: { api: 'RentalManagerUserPropertyEditV2WebApiHandler_phone' } },
  });

  useEffect(() => {
    if (contactPhone) {
      setVerifiedPhoneNumber(contactPhone);
      setDatePhoneVerified(contactPhoneDateVerified);
    }
  }, [contactPhone, contactPhoneDateVerified]);

  const updatePhoneNumber = async (phoneNumber: string) => {
    await editPhoneNumber({
      postData: {
        phone: phoneNumber,
        listingAlias,
      },
    }).then((response) => {
      setVerifiedPhoneNumber(response.contactPhone ?? phoneNumber);
      setDatePhoneVerified(response.verifiedMs ? new Date(response.verifiedMs).toString() : new Date().toString());
    });
  };

  return (
    <>
      {(verifiedPhoneNumber || !isInputFormDisabled) && (
        <>
          {contactPhoneRef && <div ref={contactPhoneRef} />}
          {verifiedPhoneNumber ? (
            <FormField
              description={
                <FormHelp>
                  {isValid(new Date(datePhoneVerified))
                    ? `Verified on ${format(new Date(datePhoneVerified), 'MMMM d, yyyy')}`
                    : 'Previously verified'}
                </FormHelp>
              }
            >
              <Label required={!isInputFormDisabled} disabled={isInputFormDisabled}>
                Phone number
              </Label>

              <Box css={{ display: 'flex' }}>
                <Input
                  disabled={true}
                  size={isScreenDesktop ? 'lg' : 'md'}
                  aria-required="true"
                  name="contactPhone"
                  type="tel"
                  value={numberUtils.formatPhoneNumberInput(verifiedPhoneNumber || '')}
                  className="fs-mask"
                  css={{
                    width: 'auto',
                    // oxlint-disable-next-line constellation/no-raw-spacing-values -- `maxWidth: 300px` is past the end of the numeric sizes scale, which stops at `5000` (200px)
                    maxWidth: '300px',
                    marginRight: '400',
                    '&[disabled]': {
                      opacity: 1,
                    },
                  }}
                />

                {!isInputFormDisabled && (
                  <Button
                    size="sm"
                    tone="brand"
                    emphasis="outlined"
                    onClick={() => setShowPhoneVerificationModal(true)}
                  >
                    Edit
                  </Button>
                )}
              </Box>
            </FormField>
          ) : (
            <FieldSet required legend={<Legend>Phone number</Legend>}>
              <Button
                size="sm"
                tone="brand"
                emphasis="outlined"
                onClick={() => setShowPhoneVerificationModal(true)}
                icon={<IconPlusCircleFilled />}
              >
                Add phone number
              </Button>
            </FieldSet>
          )}
        </>
      )}

      {showPhoneVerificationModal && (
        <VerificationModal
          headerText="Add and verify phone number"
          initialContentToVerify={verifiedPhoneNumber}
          onContentValueAdded={updatePhoneNumber}
          onVerificationConfirmation={updatePhoneNumber}
          showVerificationModal={setShowPhoneVerificationModal}
          stepsConfig={DEFAULT_VERIFICATION_CONFIG}
          verificationSource={PhoneVerificationSource.LISTING_CONTACT}
          listingDetails={listingDetails}
        />
      )}

      {!isInputFormDisabled && verifiedPhoneNumber && !isRoomForRent && (
        <Box css={{ marginTop: '400' }}>
          <PhoneNumberDisplayInfo />
        </Box>
      )}
    </>
  );
};

export default AddEditPhoneNumber;
